import React from 'react';
import { InputNumber, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import CommercialQuantityEditor from '../orders/components/CommercialQuantityEditor';
import { formatUnit, normalizeUnitCode } from '../../utils/units';
import { formatConversionValue } from '../../utils/dimensionConversion';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../utils/polishNumberInput';
import { COMMERCIAL_UNITS } from '../../utils/commercialQuantity';
import {
  WAREHOUSE_QUANTITY_MIN,
  WAREHOUSE_QUANTITY_PRECISION,
  roundWarehouseQuantity,
} from '../../utils/warehouseQuantity';
import {
  WarehouseQuantityLine,
  WarehouseQuantityPatch,
  buildWarehouseQuantityPatch,
  buildWarehouseQuantityValue,
  getWarehouseLineUnit,
  supportsWarehouseConversion,
} from './warehouseQuantityConversion';

interface WarehouseQuantityFieldProps {
  line: WarehouseQuantityLine;
  onChange: (patch: WarehouseQuantityPatch) => void;
  maxQuantity: number;
  maxConvertedQuantity: number;
  disabled?: boolean;
  /**
   * Render the unit as an operator-editable Select.
   *
   * TRUE for WZ free-text lines, where Valentyna may issue e.g. "500 kg".
   * Catalog-linked WZ lines are pinned to Product.baseUnit by backend create,
   * update and release checks, so their unit is displayed but not selectable.
   * FALSE for PZ, whose physical unit is always assigned server-side.
   */
  unitEditable?: boolean;
  /** wz:conversion.noDimensions / pz:conversion.noDimensions — the caller owns its namespace. */
  noDimensionsLabel: string;
  unitLabel: string;
  pricingQuantity?: number | null;
  pricingUnit?: string | null;
}

// Offered on free-text lines only. The old WZ unit Select had pcs/kg/m/m2/m3/l;
// 'mb' is added because LINEAR_METER products now normalize to it.
const FREE_TEXT_UNIT_CODES = ['pcs', 'kg', 'm', 'mb', 'm2', 'm3', 'l'];

/**
 * The quantity control on a WZ / PZ document line.
 *
 * Replaces the old "Ilość + Jednostka + Przelicznik" trio of loose controls with
 * the quantity plus the same "Przelicznik jednostek" modal the order lines
 * already use. That modal is bidirectional: the operator can type pieces and
 * read m², or type m² and let it work out the pieces, offering to round to whole
 * ones — the old per-line Select only ever computed one way and showed the
 * result as small grey text.
 *
 * The unit renders as a Select where the document allows it (WZ) and as the
 * quantity's own suffix where it does not (PZ, whose unit is server-assigned).
 */
const WarehouseQuantityField: React.FC<WarehouseQuantityFieldProps> = ({
  line,
  onChange,
  maxQuantity,
  maxConvertedQuantity,
  disabled = false,
  unitEditable = false,
  noDimensionsLabel,
  unitLabel,
  pricingQuantity,
  pricingUnit,
}) => {
  const { i18n } = useTranslation(['orders']);
  const baseUnit = getWarehouseLineUnit(line);
  const quantity = Number(line.quantity) || 0;

  const showUnitPicker = unitEditable && !line.productVariantId;
  const unitPicker = showUnitPicker ? (
    <label style={{ display: 'block' }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{unitLabel}</Typography.Text>
      <Select
      aria-label={unitLabel}
      value={normalizeUnitCode(line.unit) ?? 'pcs'}
      onChange={(unit) => onChange({
        quantity: roundWarehouseQuantity(line.quantity),
        // A unit switch invalidates any conversion derived from the old one.
        conversionUnit: null,
        unit,
      })}
      disabled={disabled}
      options={FREE_TEXT_UNIT_CODES.map((unit) => ({
        value: unit,
        label: formatUnit(unit, i18n.language),
      }))}
        style={{ width: '100%' }}
      />
    </label>
  ) : null;

  // A weight- or length-based line has no area or volume to derive, so the
  // conversion modal would only offer a dead end. Fall back to a plain quantity
  // input — the operator must still be able to edit it.
  if (!supportsWarehouseConversion(line)) {
    return (
      <>
        <InputNumber
          value={quantity}
          onChange={(next) => onChange({
            quantity: roundWarehouseQuantity(next),
            conversionUnit: null,
          })}
          disabled={disabled}
          min={WAREHOUSE_QUANTITY_MIN}
          max={maxQuantity}
          step={WAREHOUSE_QUANTITY_MIN}
          precision={WAREHOUSE_QUANTITY_PRECISION}
          addonAfter={showUnitPicker ? undefined : formatUnit(line.unit, i18n.language)}
          style={{ width: '100%' }}
          {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
        />
        {unitPicker}
      </>
    );
  }

  const conversionLabel = line.conversionUnit
    ? formatConversionValue(line.conversionUnit, { ...line, unit: baseUnit }, quantity)
    : '';

  return (
    <>
      <CommercialQuantityEditor
        value={buildWarehouseQuantityValue({ ...line, pricingQuantity, pricingUnit })}
        onChange={(next) => onChange(buildWarehouseQuantityPatch(next))}
        maxPhysicalQuantity={maxQuantity}
        maxPricingQuantity={maxConvertedQuantity}
        physicalUnit={baseUnit}
        lockPhysicalUnit
        pricingConversionUnits={COMMERCIAL_UNITS}
        disabled={disabled}
        compact
        width={line.width}
        length={line.length}
        height={line.height}
        thickness={line.thickness}
      />
      {unitPicker}
      {line.conversionUnit && !conversionLabel && (
        <Typography.Text type="warning" style={{ fontSize: 12 }}>
          {noDimensionsLabel}
        </Typography.Text>
      )}
    </>
  );
};

export default WarehouseQuantityField;
